import type { ReactNode } from "react";
import type { Run } from "@/app/gradingQueries";
import { GRID_LOOK, statusOf, type TaskState } from "./model";

const Eyebrow = ({ children }: { children: string }) => <span className="font-mono text-[11px] uppercase tracking-[.06em] text-mut">{children}</span>;

function Frame({ children }: { children: ReactNode }) {
  return (
    <section className="mb-5 rounded-md border border-bd bg-card p-[18px]">
      <div className="mb-3.5 flex items-baseline justify-between">
        <Eyebrow>сетка задач</Eyebrow>
        <span className="font-mono text-[11.5px] text-mut">клик — открыть задачу в ноутбуке</span>
      </div>
      {children}
      <div className="mt-4 flex flex-wrap gap-4 border-t border-bd pt-3.5 font-mono text-[11px] text-mut">
        {(["passed", "failed", "idle"] as TaskState[]).map((s) => (
          <span key={s} className="flex items-center gap-[7px]">
            <span className="size-2.5 rounded-[2px]" style={{ background: GRID_LOOK[s].bg, border: `1px ${GRID_LOOK[s].style} ${GRID_LOOK[s].bd}` }} />
            {GRID_LOOK[s].label}
          </span>
        ))}
      </div>
    </section>
  );
}

/** Сетка задач 7 колонок (S6): клик — задача в S5. */
export function TaskGrid({ run, total, focus, onPick }: { run: Run; total: number; focus?: number; onPick: (n: number) => void }) {
  return (
    <Frame>
      <div className="grid grid-cols-7 gap-2.5" data-testid="task-grid">
        {Array.from({ length: total }, (_, i) => {
          const n = i + 1;
          const st = statusOf(run, n);
          const look = GRID_LOOK[st];
          return (
            <button
              key={n}
              type="button"
              data-status={st}
              aria-label={`Задача ${n}: ${look.label}`}
              onClick={() => onPick(n)}
              className="flex cursor-pointer flex-col items-center gap-1.5 rounded-md px-2 py-3"
              style={{ border: `1px ${look.style} ${look.bd}`, background: look.bg, outline: focus === n ? "2px solid var(--acc)" : "none", color: look.fg }}
            >
              <span className="font-mono text-[15px] font-semibold" style={{ color: look.fg }}>
                {n}
              </span>
              <span className="font-mono text-[10.5px]" style={{ color: look.fg }}>
                {look.mark}
              </span>
            </button>
          );
        })}
      </div>
    </Frame>
  );
}

/** Скелет сетки, пока раннер выполняет ячейки. */
export function GridSkeleton({ total }: { total: number }) {
  return (
    <Frame>
      <div className="grid grid-cols-7 gap-2.5" data-testid="grid-skeleton" aria-busy="true">
        {Array.from({ length: total }, (_, i) => (
          <span key={i} className="h-[58px] rounded-md border border-dashed border-bd2 bg-card2" style={{ animation: "e1pulse 1.6s ease-in-out infinite", animationDelay: `${(i % 7) * 0.08}s` }} />
        ))}
      </div>
      <style>{`@keyframes e1pulse{0%,100%{opacity:.35}50%{opacity:.8}}`}</style>
    </Frame>
  );
}
